import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { listenForegroundMessages } from '@/lib/pushNotifications';
import { isOtherAudience } from '@/components/subcontractor/PushClickDialog';

// Headless. Shows the push that arrives while the portal is OPEN.
//
// Pushes are sent data-only, so the service worker's onBackgroundMessage draws
// the notification — but the browser only runs that when the page is in the
// background. With the app in front, Firebase routes the message to onMessage
// instead, and nothing was listening: listenForegroundMessages existed in the
// push library and was never called anywhere. A driver looking at the portal
// when their route landed got no push, no toast, nothing.
//
// The toast carries the same deep link as the notification, so the route is one
// tap away whether or not the phone drew a banner.
const PushForegroundListener = () => {
  const { profile } = useSubcontractorProfile();
  const subId = profile?.role === 'subcontractor' ? profile.id : null;
  const { toast } = useToast();
  const navigate = useNavigate();
  const seen = useRef(new Set());

  useEffect(() => {
    if (!subId) return;
    let unsubscribe = null;
    let cancelled = false;

    listenForegroundMessages((payload) => {
      const d = payload?.data || {};
      // Pushes for admins or partners never show in the crew app (a shared phone, 20261327).
      if (isOtherAudience(d.audience)) return;
      const title = d.title || payload?.notification?.title || 'Parcelink';
      const body = d.body || payload?.notification?.body || '';

      // FCM can redeliver; a route ask that toasts twice reads as two routes.
      const key = `${d.accept_message_id || ''}|${d.sent_at || ''}|${title}`;
      if (seen.current.has(key)) return;
      seen.current.add(key);

      // The link arrives as an absolute portal URL; navigate() needs the path.
      let path = null;
      try {
        path = d.link ? new URL(d.link, window.location.origin).pathname + (new URL(d.link, window.location.origin).search || '') : null;
      } catch { path = null; }

      toast({
        title,
        description: body,
        duration: d.kind === 'route_acceptance' ? 30000 : 8000,
        action: path
          ? (
            <Button size="sm" variant="secondary" onClick={() => navigate(path)}>
              {d.kind === 'route_acceptance' ? 'Accept' : 'Open'}
            </Button>
          )
          : undefined,
      });
    })
      .then((off) => {
        if (cancelled) { try { off?.(); } catch { /* already gone */ } return; }
        unsubscribe = off;
      })
      // Unsupported browser, denied permission, or no service worker: the push
      // simply is not delivered here. Never break the page over it.
      .catch(() => {});

    return () => {
      cancelled = true;
      try { unsubscribe?.(); } catch { /* already gone */ }
    };
  }, [subId, toast, navigate]);

  return null;
};

export default PushForegroundListener;
